(ns common.schema.entity-type-registry
  "Static common registry of entity-type definitions.
   - Avoids init-order issues by enumerating definitions explicitly.
   - **Validation is opt-in:** call `validate-registry!` from tests or startup hooks.
   - Guards against duplicate :entity-type keys via the validator.
  "
  (:require
   [common.schema.entities.account.core :as account]
   [common.schema.entities.ai-document-generation.core :as ai-document-generation]
   [common.schema.entities.aifm-decision.core :as aifm-decision]
   [common.schema.entities.ai-summary-configuration.core :as ai-summary-configuration]
   [common.schema.entities.ai-task-config-override.core :as ai-task-config-override]
   [common.schema.entities.calculated.indirect-holding.core :as calculated-indirect-holding]
   [common.schema.entities.company.core :as company]
   [common.schema.entities.counterparty-diligence.company.core :as company-counterparty-diligence]
   [common.schema.entities.counterparty-diligence.person.core :as person-counterparty-diligence]
   [common.schema.entities.counterparty-diligence.project.core :as project-counterparty-diligence]
   [common.schema.entities.workspace.kyc.contract-kyc-profile.core :as contract-kyc-profile]
   [common.schema.entities.workspace.kyc.tenant-kyc-profile.core :as tenant-kyc-profile]
   [common.schema.entities.esign-process.core :as esign-process]
   [common.schema.entities.exit-opportunity.core :as exit-opportunity]
   [common.schema.entities.fee.core :as fee]
   [common.schema.entities.fof-asset.core :as fof-asset]
   [common.schema.entities.fund.core :as fund]
   [common.schema.entities.fund-commitment.core :as fund-commitment]
   [common.schema.entities.fundraising-opportunity.core :as fundraising-opportunity]
   [common.schema.entities.holding.core :as holding]
   [common.schema.entities.ic-decision.core :as ic-decision]
   [common.schema.entities.investor.core :as investor]
   [common.schema.entities.oppo.core :as oppo]
   [common.schema.entities.other-asset.core :as other-asset]
   [common.schema.entities.person.core :as person]
   [common.schema.entities.real-estate.core :as real-estate]
   [common.schema.entities.risk.core :as risk]
   [common.schema.entities.risk-holding.core :as risk-holding]
   [common.schema.entities.sale-agreement.core :as sale-agreement]
   [common.schema.entities.sample-entity.core :as sample-entity]
   [common.schema.entities.rotation-period.core :as rotation-period]
   [common.schema.entities.rotation-period-override.core :as rotation-period-override]
   [common.schema.entities.sales-opportunity.core :as sales-opportunity]
   [common.schema.entities.reminder-rule.core :as reminder-rule]
   [common.schema.entities.screening-profile.core :as screening-profile]
   [common.schema.entities.survey-batch.core :as survey-batch]
   [common.schema.entities.survey-cycle.core :as survey-cycle]
   [common.schema.entities.survey-reminder.core :as survey-reminder]
   [common.schema.entities.survey.survey-question.core :as survey-question]
   [common.schema.entities.transaction-process.core :as transaction-process]
   [common.schema.entity-type-spec :as et-spec]
   [common.schema.entities.kyc.kyc-profile.core :as kyc-profile]
   [common.schema.entities.task.core :as task]
   [common.schema.entities.value-creation.core :as value-creation]
   [common.schema.entities.vault.zap-vault.core :as zap-vault]
   [malli.core :as m]))

(def entity-type-definitions
  [account/entity-type-definition
   ai-document-generation/entity-type-definition
   aifm-decision/entity-type-definition
   ai-summary-configuration/entity-type-definition
   ai-task-config-override/entity-type-definition
   calculated-indirect-holding/entity-type-definition
   company/entity-type-definition
   company-counterparty-diligence/entity-type-definition
   person-counterparty-diligence/entity-type-definition
   project-counterparty-diligence/entity-type-definition
   contract-kyc-profile/entity-type-definition
   tenant-kyc-profile/entity-type-definition
   esign-process/entity-type-definition
   exit-opportunity/entity-type-definition
   fee/entity-type-definition
   fof-asset/entity-type-definition
   fund/entity-type-definition
   fund-commitment/entity-type-definition
   fundraising-opportunity/entity-type-definition
   holding/entity-type-definition
   ic-decision/entity-type-definition
   investor/entity-type-definition
   oppo/entity-type-definition
   other-asset/entity-type-definition
   person/entity-type-definition
   real-estate/entity-type-definition
   risk/entity-type-definition
   risk-holding/entity-type-definition
   sale-agreement/entity-type-definition
   rotation-period/entity-type-definition
   rotation-period-override/entity-type-definition
   sales-opportunity/entity-type-definition
   reminder-rule/entity-type-definition
   screening-profile/entity-type-definition
   sample-entity/entity-type-definition
   survey-batch/entity-type-definition
   survey-cycle/entity-type-definition
   survey-reminder/entity-type-definition
   survey-question/entity-type-definition
   transaction-process/entity-type-definition
   kyc-profile/entity-type-definition
   task/entity-type-definition
   value-creation/entity-type-definition
   zap-vault/entity-type-definition])

(defn- duplicate-entity-types
  [defs]
  (->> defs
       (map :entity-type)
       (frequencies)
       (keep (fn [[k cnt]] (when (> cnt 1) k)))
       (vec)))

(def ^:private validation-field-keys
  ;; Validation allows both target field semantics and legacy schema metadata.
  ;; Runtime consumers should interpret legacy mandatory metadata through
  ;; common.schema.field-spec instead of reading :field/mandatory directly.
  [:field/attribute
   :field/data-type
   :field/entity-types
   :field/name
   :field/cardinality
   :field/system-semantics
   :field/system-required?
   :field/system-mandatory
   :field/tenant-admin-configuration
   :field/mandatory
   :field/not-clearable?
   :field/required-at-creation?
   :field/no-history?
   :ui/default-editor-type
   :ui/allowed-editor-types
   :ui/default-filter-type
   :ui/allowed-filter-types
   :validation-spec
   :field/translations
   :field/default-visibility
   :field/default-authorization
   :field/constraints
   :field/system-controlled?
   :field/tenant-specific?
   :field/active-by-default?
   :field/live-update-paths])

(defn- field-visibility-value
  [field-spec visibility-key]
  (or (get field-spec visibility-key)
      (get-in field-spec [:field/default-visibility visibility-key])))

(defn- normalize-field-spec-for-validation
  [attribute field-spec]
  (let [default-editor-type (field-visibility-value field-spec :editor)
        default-filter-type (field-visibility-value field-spec :filter)
        normalized-field-spec (cond-> field-spec
                                attribute
                                (assoc :field/attribute attribute)

                                (and default-editor-type
                                     (not (contains? field-spec :ui/default-editor-type)))
                                (assoc :ui/default-editor-type default-editor-type)

                                (and default-editor-type
                                     (not (contains? field-spec :ui/allowed-editor-types)))
                                (assoc :ui/allowed-editor-types #{default-editor-type})

                                (and default-filter-type
                                     (not (contains? field-spec :ui/default-filter-type)))
                                (assoc :ui/default-filter-type default-filter-type)

                                (and default-filter-type
                                     (not (contains? field-spec :ui/allowed-filter-types)))
                                (assoc :ui/allowed-filter-types #{default-filter-type}))]
    (reduce (fn [acc k]
              (if (contains? normalized-field-spec k)
                (assoc acc k (get normalized-field-spec k))
                acc))
            {}
            validation-field-keys)))

(defn- schema-entry->validation-tuples
  [entry]
  (cond
    (and (vector? entry)
         (= 2 (count entry))
         (keyword? (first entry))
         (map? (second entry)))
    [[(first entry) (normalize-field-spec-for-validation (first entry) (second entry))]]

    (vector? entry)
    (->> entry
         (mapcat schema-entry->validation-tuples)
         vec)

    :else
    []))

(defn- normalize-definition-for-validation
  [definition]
  (update definition
          :schema
          (fn [schema]
            (->> schema
                 (mapcat schema-entry->validation-tuples)
                 vec))))

;; Public validation function (opt-in)
(defn validate-registry!
  "Validate a sequence of entity-type definitions. Throws with Malli explain data
   on failure. Intended to be called from tests or explicit startup hooks, not at
   class-load time."
  [defs]
  (doseq [d defs]
    (let [normalized-definition (normalize-definition-for-validation d)]
      (when-not (m/validate et-spec/entity-type-definition-model normalized-definition)
        (throw (ex-info "Invalid common entity-type definition"
                        {:entity-type (:entity-type d)
                         :errors      (m/explain et-spec/entity-type-definition-model normalized-definition)})))))
  (when-let [dupes (seq (duplicate-entity-types defs))]
    (throw (ex-info "Duplicate :entity-type keys in common registry"
                    {:duplicates dupes})))
  :ok)

;; Build the map from definitions. Validation is **not** performed at load time.
(def entity-types
  "Map of entity-type keyword -> common definition. Validation is **not** performed at load time.
   Call `validate-registry!` from tests or an explicit startup hook if desired."
  (into {}
        (map (fn [etd]
               [(:entity-type etd) etd])
             entity-type-definitions)))

;; existing helpers assumed:
;; (definitions) => {entity-type {:schema … :icon … :search … :read-permissions-check-fn … :disable-permissions-check-fn …}}
;; (definition et) => single def

(defn entity-type-definitions-backwards-compatible
  "Adapt new common entity-type definitions into the legacy backend-facing
  entity-types map shape used by `common.schema.entity-types`.

  For each common definition, we project the keys that the legacy map expects:
    - :entity/schema                        (from :schema)
    - :entity/icon                          (from :icon)
    - :entity/read-permissions-check-fn     (from :read-permissions-check-fn)
    - :entity/disable-permissions-check-fn  (from :disable-permissions-check-fn)
    - :entity/mcp-blacklisted?              (from :entity/mcp-blacklisted?)
    - :entity/mcp-read-permissions-check-fn (from :entity/mcp-read-permissions-check-fn)
    - :entity/search-index?                 (true if :search is present,
                                             or use explicit :search-index? if provided)

  Intentionally conservative: only surface attributes are safe for old callers.
  Extend this projection here if you later need more legacy keys."
  []
  (->> entity-types
       (map (fn [[et def]]
              [et
               (cond-> {}
                 (:schema def)
                 (assoc :entity/schema (:schema def))

                 (:icon def)
                 (assoc :entity/icon (:icon def))

                 (:read-permissions-check-fn def)
                 (assoc :entity/read-permissions-check-fn
                        (:read-permissions-check-fn def))

                 (:disable-permissions-check-fn def)
                 (assoc :entity/disable-permissions-check-fn
                        (:disable-permissions-check-fn def))

                 (contains? def :entity/mcp-blacklisted?)
                 (assoc :entity/mcp-blacklisted? (:entity/mcp-blacklisted? def))

                 (:entity/mcp-read-permissions-check-fn def)
                 (assoc :entity/mcp-read-permissions-check-fn
                        (:entity/mcp-read-permissions-check-fn def))

                       ;; Prefer an explicit flag if present; otherwise infer from :search block.
                 (contains? def :search-index?)
                 (assoc :entity/search-index? (:search-index? def))

                 (and (not (contains? def :search-index?))
                      (contains? def :search))
                 (assoc :entity/search-index? true))]))
       (into {})))

(def entity-type-names-for-backwards-compatibility
  (letfn [(definition-name-translations
            [definition]
            (or (:translations (:name definition))
                (:name definition)
                {}))
          (definition-plural-translations
            [definition]
            (or (:translations (:plural definition))
                (:plural definition)
                {}))]
    (->> entity-types
         (map (fn [[et definition]]
                [et (cond-> {:field/translations (definition-name-translations definition)}
                      (seq (definition-plural-translations definition))
                      (assoc :plural (definition-plural-translations definition)))]))
         (into {}))))

(defn definition
  [entity-type]
  (get entity-types entity-type))
